import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Paper, Snackbar, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Typography, MenuItem, InputAdornment, IconButton,
    Grid, Tabs, Tab
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Send as ReleaseIcon,
    Undo as RecallIcon,
    VisibilityOutlined as ViewIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    Print as PrintIcon,
    ReceiptLong as PayslipIcon,
    CalculateOutlined as AuditIcon,
    Download as DownloadIcon,
    PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import AppHeader from '../../components/AppHeader';
import { useTenantContext } from '../../context/TenantContext';
import { myanmarFontBase64 } from '../../utils/myanmarFontBase64';
import EffectiveDatedPayrollBreakdown from '../../components/payroll/EffectiveDatedPayrollBreakdown';

const BUILT_IN_COMPONENTS = [
    { code: 'BASIC_SALARY', name: 'Basic Salary', effect: 'EARNING' },
    { code: 'OT_PAY', name: 'Overtime Pay', effect: 'EARNING' },
    { code: 'MEAL_ALLOWANCE', name: 'Meal Allowance', effect: 'EARNING' },
    { code: 'TRANSPORT_ALLOWANCE', name: 'Transport Allowance', effect: 'EARNING' },
    { code: 'SPECIAL_ALLOWANCE', name: 'Special Allowance', effect: 'EARNING' },
    { code: 'OTHER_EARNINGS', name: 'Other Earnings', effect: 'EARNING' },
    { code: 'LATE_EARLY_PENALTIES', name: 'Late/Early Penalty', effect: 'DEDUCTION' },
    { code: 'UNPAID_LEAVE_DEDUCTION', name: 'Unpaid Leave Deduction', effect: 'DEDUCTION' },
    { code: 'SSB_DEDUCTION', name: 'SSB Deduction', effect: 'DEDUCTION' },
    { code: 'TAX_DEDUCTION', name: 'Income Tax', effect: 'DEDUCTION' },
    { code: 'OTHER_DEDUCTIONS', name: 'Other Deductions', effect: 'DEDUCTION' }
];

const PayslipDistribution = ({ setIsLoggedIn }) => {
    const { tenantProfile } = useTenantContext();
    const tenant = useMemo(() => localStorage.getItem('tenant') || '', []);
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRun, setSelectedRun] = useState(null);
    const [payslips, setPayslips] = useState([]);
    const [payslipSearch, setPayslipSearch] = useState('');
    const [payElements, setPayElements] = useState([]);
    const [corrections, setCorrections] = useState([]);
    const [correctionPayslip, setCorrectionPayslip] = useState(null);
    const [breakdownPayroll, setBreakdownPayroll] = useState(null);
    const [payslipTab, setPayslipTab] = useState(0);
    const [editingCorrection, setEditingCorrection] = useState(null);
    const [correctionForm, setCorrectionForm] = useState({
        reason: '',
        component_code: 'OT_PAY',
        effect: 'EARNING',
        element_id: null,
        amount_delta: '',
        quantity: '',
        rate: ''
    });
    const [message, setMessage] = useState({ open: false, severity: 'success', text: '' });
    const [periodFilter, setPeriodFilter] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
    });

    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' }, { value: 4, label: 'April' },
        { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' },
        { value: 9, label: 'September' }, { value: 10, label: 'October' },
        { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];

    const years = [2025, 2026, 2027];

    const requestConfig = useMemo(() => ({
        withCredentials: true,
        headers: { 'X-Tenant': tenant }
    }), [tenant]);

    const loadRuns = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/runs`, requestConfig);
            const distributable = (response.data.data || []).filter(run => ['FINALIZED', 'PAID'].includes(run.status));
            setRuns(distributable);
        } catch (error) {
            setMessage({ open: true, severity: 'error', text: error.response?.data?.message || 'Could not load finalized payroll runs' });
        } finally {
            setLoading(false);
        }
    }, [requestConfig]);

    const loadCorrections = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/corrections`, {
                ...requestConfig,
                params: {
                    month: periodFilter.month,
                    year: periodFilter.year
                }
            });
            setCorrections(response.data.data || []);
        } catch (error) {
            setMessage({ open: true, severity: 'error', text: error.response?.data?.message || 'Could not load payslip corrections' });
        }
    }, [requestConfig, periodFilter.month, periodFilter.year]);

    const loadPayElements = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/elements`, requestConfig);
            if (response.data?.success) {
                setPayElements(response.data.data || []);
            }
        } catch (error) {
            console.error('Could not load payroll elements:', error);
        }
    }, [requestConfig]);

    useEffect(() => { loadRuns(); }, [loadRuns]);
    useEffect(() => { loadCorrections(); }, [loadCorrections]);
    useEffect(() => { loadPayElements(); }, [loadPayElements]);

    // Combine built-in standard payroll calculation components with all active pay elements from DB
    const allComponents = useMemo(() => {
        const list = BUILT_IN_COMPONENTS.map(c => ({ ...c, id: null }));
        (payElements || []).forEach(elem => {
            const elemCode = String(elem.element_code || '').trim();
            const elemName = elem.element_name || elem.element_code;
            const elemType = String(elem.element_type || 'EARNING').toUpperCase();
            const matchedIndex = list.findIndex(c => c.code.toUpperCase() === elemCode.toUpperCase());
            if (matchedIndex >= 0) {
                list[matchedIndex].id = elem.id;
            } else {
                list.push({
                    id: elem.id,
                    code: elemCode,
                    name: elemName,
                    effect: elemType
                });
            }
        });
        return list;
    }, [payElements]);

    const viewPayslips = async (run) => {
        setLoading(true);
        setPayslipSearch('');
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/runs/${run.id}/payslips`, requestConfig);
            setPayslips(response.data.data || []);
            setSelectedRun(run);
        } catch (error) {
            setMessage({ open: true, severity: 'error', text: 'Could not load payslips' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenPayslipModal = (payslip) => {
        setBreakdownPayroll(payslip);
        setPayslipTab(0);
    };

    const handleOpenCorrect = (payslip) => {
        setEditingCorrection(null);
        setCorrectionPayslip(payslip);
        const defaultComp = allComponents.find(c => c.code === 'OT_PAY') || allComponents[0] || { code: 'OT_PAY', effect: 'EARNING', id: null };
        setCorrectionForm({
            reason: '',
            component_code: defaultComp.code,
            effect: defaultComp.effect,
            element_id: defaultComp.id || null,
            amount_delta: '',
            quantity: '',
            rate: ''
        });
    };

    const handleComponentChange = (code) => {
        const selected = allComponents.find(c => c.code === code);
        setCorrectionForm(prev => ({
            ...prev,
            component_code: code,
            effect: selected?.effect || 'EARNING',
            element_id: selected?.id || null
        }));
    };

    const createCorrection = async () => {
        const selected = allComponents.find(c => c.code === correctionForm.component_code);
        setLoading(true);
        try {
            const payload = {
                reason: correctionForm.reason,
                items: [{
                    element_id: selected?.id || correctionForm.element_id || null,
                    component_code: correctionForm.component_code,
                    component_name: selected?.name || correctionForm.component_code,
                    effect: selected?.effect || correctionForm.effect,
                    amount_delta: Number(correctionForm.amount_delta),
                    quantity: correctionForm.quantity || null,
                    rate: correctionForm.rate || null
                }]
            };
            if (editingCorrection) {
                await axios.put(`${process.env.REACT_APP_API_URL1}/sm/payroll/corrections/${editingCorrection.id}`, payload, requestConfig);
            } else {
                await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/payslips/${correctionPayslip.id}/corrections`, payload, requestConfig);
            }
            setCorrectionPayslip(null);
            setEditingCorrection(null);
            setCorrectionForm({ reason: '', component_code: 'OT_PAY', effect: 'EARNING', element_id: null, amount_delta: '', quantity: '', rate: '' });
            setSelectedRun(null);
            await Promise.all([loadRuns(), loadCorrections()]);
            setMessage({ open: true, severity: 'success', text: editingCorrection ? 'Correction draft updated' : 'Payslip recalled and correction draft created' });
        } catch (error) {
            setMessage({ open: true, severity: 'error', text: error.response?.data?.message || 'Could not create correction' });
        } finally {
            setLoading(false);
        }
    };

    const editCorrection = (correction) => {
        const item = correction.items?.[0] || {};
        setEditingCorrection(correction);
        setCorrectionPayslip({
            id: correction.original_payslip_id,
            staff_code: correction.staff_code,
            employee_number: correction.employee_number,
            full_name: correction.full_name
        });
        const compCode = item.component_code || 'OT_PAY';
        const matchedComp = allComponents.find(c => c.code === compCode || (item.element_id && c.id === item.element_id));
        setCorrectionForm({
            reason: correction.reason || '',
            component_code: matchedComp?.code || compCode,
            effect: item.effect || matchedComp?.effect || 'EARNING',
            element_id: item.element_id || matchedComp?.id || null,
            amount_delta: item.amount_delta ?? '',
            quantity: item.quantity ?? '',
            rate: item.rate ?? ''
        });
    };

    const exportCorrection = async (correction) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/correction-accounting-export?correction_id=${correction.id}`, { ...requestConfig, responseType: 'blob' });
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `payroll_correction_${correction.id}_delta.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
            await loadCorrections();
        } catch (error) {
            setMessage({ open: true, severity: 'error', text: 'Could not export correction accounting delta' });
        }
    };

    const correctionAction = async (correction, action) => {
        let endpoint = `${process.env.REACT_APP_API_URL1}/sm/payroll/corrections/${correction.id}/status`;
        let body = { status: action };
        if (action === 'REJECTED') {
            const reason = window.prompt('Rejection reason');
            if (!reason) return;
            body.comment = reason;
        }
        if (action === 'APPLY') { endpoint = `${process.env.REACT_APP_API_URL1}/sm/payroll/corrections/${correction.id}/apply`; body = {}; }
        if (action === 'RELEASE') { endpoint = `${process.env.REACT_APP_API_URL1}/sm/payroll/corrections/${correction.id}/release`; body = {}; }
        setLoading(true);
        try {
            if (['APPLY','RELEASE'].includes(action)) await axios.post(endpoint, body, requestConfig);
            else await axios.put(endpoint, body, requestConfig);
            await Promise.all([loadRuns(), loadCorrections()]);
            setMessage({ open: true, severity: 'success', text: `Correction ${action.toLowerCase()} completed` });
        } catch (error) {
            setMessage({ open: true, severity: 'error', text: error.response?.data?.message || 'Correction action failed' });
        } finally {
            setLoading(false);
        }
    };

    const changeRelease = async (run, release) => {
        const action = release ? 'release' : 'recall';
        if (!window.confirm(`Are you sure you want to ${action} all payslips for run #${run.id}?`)) return;
        setLoading(true);
        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL1}/sm/payroll/runs/${run.id}/release`,
                { isReleased: release },
                requestConfig
            );
            setMessage({ open: true, severity: 'success', text: `Payslips ${release ? 'released to staff' : 'recalled from staff'} successfully` });
            setSelectedRun(null);
            await loadRuns();
        } catch (error) {
            setMessage({ open: true, severity: 'error', text: error.response?.data?.message || `Could not ${action} payslips` });
            setLoading(false);
        }
    };

    // Filter payslips inside the View modal by employee number, staff code, name, department
    const filteredPayslips = useMemo(() => {
        if (!payslipSearch.trim()) return payslips;
        const term = payslipSearch.toLowerCase().trim();
        return payslips.filter(p =>
            (p.employee_number || '').toLowerCase().includes(term) ||
            (p.staff_code || '').toLowerCase().includes(term) ||
            (p.full_name || '').toLowerCase().includes(term) ||
            (p.dept_name || '').toLowerCase().includes(term)
        );
    }, [payslips, payslipSearch]);

    // PDF Payslips Generator
    const generatePayslipsPDF = async (runData, payslipRecords) => {
        try {
            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a5', compress: true });
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            const periodMonthNum = Number(runData?.period_month || selectedRun?.period_month || 1);
            const periodYearNum = runData?.period_year || selectedRun?.period_year || new Date().getFullYear();
            const periodName = `${monthNames[periodMonthNum - 1]} ${periodYearNum}`;

            const loadLogo = () => {
                return new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => resolve(img);
                    img.onerror = () => resolve(null);
                    img.src = tenantProfile?.logo_url || '/logo.png';
                });
            };

            const img = await loadLogo();
            const logoAlias = img ? 'company_logo' : null;

            if (myanmarFontBase64) {
                doc.addFileToVFS('Padauk-Regular.ttf', myanmarFontBase64);
                doc.addFont('Padauk-Regular.ttf', 'Padauk', 'normal');
                doc.addFont('Padauk-Regular.ttf', 'Padauk', 'bold');
            }
            const defaultFont = myanmarFontBase64 ? 'Padauk' : 'helvetica';

            payslipRecords.forEach((slip, index) => {
                if (index > 0) doc.addPage();

                if (img) {
                    try {
                        doc.addImage(img, 'JPEG', 14, 5, 26, 26, logoAlias, 'FAST');
                    } catch (e) {
                        console.warn('Logo failed to render in PDF', e);
                    }
                }

                doc.setFontSize(18);
                doc.setTextColor(tenantProfile?.primary_color || '#4338ca');
                doc.text(tenantProfile?.tenant_name || 'PeopleHub', 105, 14, { align: 'center' });

                doc.setFontSize(13);
                doc.setTextColor(15, 23, 42);
                doc.text('PAY ADVICE / PAYSLIP', 105, 22, { align: 'center' });

                doc.setFontSize(9);
                doc.setTextColor(100);
                doc.text(`Period: ${periodName}  |  Version: v${slip.version_number || 1}`, 105, 28, { align: 'center' });

                doc.setDrawColor(226, 232, 240);
                doc.line(14, 34, 196, 34);

                doc.setFont(defaultFont, 'bold');
                doc.text('Employee No:', 14, 40);
                doc.setFont(defaultFont, 'normal');
                doc.text(slip.employee_number || slip.staff_code || '-', 42, 40);

                doc.setFont(defaultFont, 'bold');
                doc.text('Employee Name:', 14, 46);
                doc.setFont(defaultFont, 'normal');
                doc.text(slip.full_name || '-', 42, 46);

                doc.setFont(defaultFont, 'bold');
                doc.text('Department:', 115, 40);
                doc.setFont(defaultFont, 'normal');
                doc.text(slip.dept_name || 'N/A', 140, 40);

                doc.setFont(defaultFont, 'bold');
                doc.text('Designation:', 115, 46);
                doc.setFont(defaultFont, 'normal');
                doc.text(slip.job_title || 'N/A', 140, 46);

                doc.line(14, 52, 196, 52);

                const earnings = [];
                const salarySegments = slip.compensation_segments || [];
                if (salarySegments.length > 1) {
                    salarySegments.forEach(segment => {
                        earnings.push([
                            `Basic Salary (${String(segment.startDate).slice(0, 10)} – ${String(segment.endDate).slice(0, 10)})`,
                            Number(segment.basicPay ?? segment.exactBasicPay ?? 0).toLocaleString()
                        ]);
                    });
                } else if ((slip.basic_salary || 0) > 0) {
                    earnings.push(['Basic Salary', (slip.basic_salary || 0).toLocaleString()]);
                }

                if ((slip.ot_pay || 0) > 0) {
                    const otHours = slip.payable_ot_mins ? ` (${(Number(slip.payable_ot_mins) / 60).toFixed(1)} hrs)` : '';
                    earnings.push([`Overtime Pay${otHours}`, Number(slip.ot_pay).toLocaleString()]);
                }
                if (Number(slip.meal_allowance || 0) > 0) earnings.push(['Meal Allowance', Number(slip.meal_allowance).toLocaleString()]);
                if (Number(slip.transport_allowance || 0) > 0) earnings.push(['Transport Allowance', Number(slip.transport_allowance).toLocaleString()]);
                if (Number(slip.special_allowance || 0) > 0) earnings.push(['Special Allowance', Number(slip.special_allowance).toLocaleString()]);

                const recurringEarnings = (slip.recurring_items || []).filter(it => it.element_type === 'EARNING' && !['BASIC', 'OT'].includes(it.element_code));
                recurringEarnings.forEach(it => {
                    if (Number(it.amount) > 0) {
                        earnings.push([it.element_name || it.element_code, Number(it.amount).toLocaleString()]);
                    }
                });

                const recurringEarningsSum = recurringEarnings.reduce((acc, it) => acc + Number(it.amount || 0), 0);
                const unallocatedOtherEarnings = Math.max(0, Number(slip.other_earnings || 0) - recurringEarningsSum);
                if (unallocatedOtherEarnings > 0) {
                    earnings.push(['Other Earnings', Number(unallocatedOtherEarnings).toLocaleString()]);
                }

                const deductions = [];
                if (Number(slip.late_early_penalties || 0) > 0) {
                    deductions.push(['Late / Early Penalty', Number(slip.late_early_penalties).toLocaleString()]);
                }
                if (Number(slip.unpaid_leave_deduction || 0) > 0) {
                    const days = slip.unpaid_leave_days ? ` (${slip.unpaid_leave_days} d)` : '';
                    deductions.push([`Unpaid Leave${days}`, Number(slip.unpaid_leave_deduction).toLocaleString()]);
                }
                if (Number(slip.unpaid_absence_deduction || 0) > 0) {
                    const days = slip.unpaid_absence_days ? ` (${slip.unpaid_absence_days} d)` : '';
                    deductions.push([`Unpaid Absence${days}`, Number(slip.unpaid_absence_deduction).toLocaleString()]);
                }
                if (Number(slip.ssb_deduction || 0) > 0) {
                    deductions.push(['Social Security (SSB)', Number(slip.ssb_deduction).toLocaleString()]);
                }
                if (Number(slip.tax_deduction || 0) > 0) {
                    deductions.push(['Income Tax (PIT)', Number(slip.tax_deduction).toLocaleString()]);
                }

                const recurringDeductions = (slip.recurring_items || []).filter(it => it.element_type === 'DEDUCTION');
                recurringDeductions.forEach(it => {
                    if (Number(it.amount) > 0) {
                        deductions.push([it.element_name || it.element_code, Number(it.amount).toLocaleString()]);
                    }
                });

                const recurringDeductionsSum = recurringDeductions.reduce((acc, it) => acc + Number(it.amount || 0), 0);
                const unallocatedOtherDeductions = Math.max(0, Number(slip.other_deductions || 0) - recurringDeductionsSum);
                if (unallocatedOtherDeductions > 0) {
                    deductions.push(['Other Deductions', Number(unallocatedOtherDeductions).toLocaleString()]);
                }

                const grossPayVal = Number(slip.gross_pay || 0);
                const netPayVal = Number(slip.net_pay || 0);
                const totalDeductionsVal = Math.max(0, grossPayVal - netPayVal);

                autoTable(doc, {
                    startY: 55,
                    head: [['Earnings & Allowances', 'Amount (MMK)']],
                    body: earnings,
                    foot: [['Total Gross Earnings', `${grossPayVal.toLocaleString()} MMK`]],
                    footStyles: { fillColor: [240, 253, 244], textColor: [22, 101, 52], font: defaultFont, fontStyle: 'bold' },
                    theme: 'striped',
                    headStyles: { fillColor: tenantProfile?.primary_color || '#4338ca', font: defaultFont },
                    margin: { left: 14, right: 110 },
                    styles: { fontSize: 8, font: defaultFont }
                });

                autoTable(doc, {
                    startY: 55,
                    head: [['Deductions & Taxes', 'Amount (MMK)']],
                    body: deductions.length ? deductions : [['No deductions applied', '0']],
                    foot: [['Total Deductions', `${totalDeductionsVal.toLocaleString()} MMK`]],
                    footStyles: { fillColor: [255, 241, 242], textColor: [190, 18, 60], font: defaultFont, fontStyle: 'bold' },
                    theme: 'striped',
                    headStyles: { fillColor: [244, 63, 94], font: defaultFont },
                    margin: { left: 110, right: 14 },
                    styles: { fontSize: 8, font: defaultFont }
                });

                const finalY = Math.max(doc.lastAutoTable.finalY || 105, 105);
                doc.setDrawColor(203, 213, 225);
                doc.setFillColor(255, 255, 255);
                doc.roundedRect(14, finalY + 4, 182, 20, 2, 2, 'FD');

                doc.setFontSize(8);
                doc.setFont(defaultFont, 'bold');
                doc.setTextColor(71, 85, 105);
                doc.text('NET TAKE-HOME PAY', 20, finalY + 11);

                doc.setFontSize(7.5);
                doc.setFont(defaultFont, 'normal');
                doc.setTextColor(100, 116, 139);
                doc.text(`Gross (${grossPayVal.toLocaleString()}) - Deductions (${totalDeductionsVal.toLocaleString()})`, 20, finalY + 19);

                doc.setFontSize(12);
                doc.setFont(defaultFont, 'bold');
                doc.setTextColor(15, 23, 42);
                doc.text(`${netPayVal.toLocaleString()} MMK`, 190, finalY + 16, { align: 'right' });

                doc.setFontSize(7);
                doc.setTextColor(148, 163, 184);
                doc.text('Confidential · PeopleHub System Generated Payslip', 105, 142, { align: 'center' });
            });

            const fileName = payslipRecords.length === 1
                ? `Payslip_${payslipRecords[0].employee_number || payslipRecords[0].staff_code}_${periodName.replace(' ', '_')}.pdf`
                : `Payslips_Run_${runData?.id || selectedRun?.id}_${periodName.replace(' ', '_')}.pdf`;
            doc.save(fileName);
        } catch (error) {
            console.error('PDF Export Crash:', error);
            setMessage({ open: true, text: `PDF Generation failed: ${error.message}`, severity: 'error' });
        }
    };

    // CSV Payslips Generator
    const exportToCSV = (runData, payslipRecords) => {
        const dynamicAlwCols = new Set();
        const dynamicDedCols = new Set();
        payslipRecords.forEach(row => {
            Object.keys(row).forEach(k => {
                if (k.startsWith('alw_')) dynamicAlwCols.add(k);
                if (k.startsWith('ded_')) dynamicDedCols.add(k);
            });
        });
        const alwArr = Array.from(dynamicAlwCols);
        const dedArr = Array.from(dynamicDedCols);
        const headers = ['Employee No', 'Name', 'Department', 'Basic', 'OT', ...alwArr.map(k => k.replace('alw_', '')), 'Late Penalty', 'Unpaid Leave', 'SSB', 'Tax', ...dedArr.map(k => k.replace('ded_', '')), 'Gross Pay', 'Total Deductions', 'Net Pay'];
        const rows = payslipRecords.map(s => [
            `"${s.employee_number || s.staff_code || ''}"`,
            `"${s.full_name || ''}"`,
            `"${s.dept_name || ''}"`,
            s.basic_salary || 0,
            s.ot_pay || 0,
            ...alwArr.map(k => s[k] || 0),
            s.late_early_penalties || 0,
            s.unpaid_leave_deduction || 0,
            s.ssb_deduction || 0,
            s.tax_deduction || 0,
            ...dedArr.map(k => s[k] || 0),
            s.gross_pay || 0,
            (Number(s.gross_pay || 0) - Number(s.net_pay || 0)),
            s.net_pay || 0
        ]);
        const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
        const link = document.createElement('a');
        link.href = encodeURI(csvContent);
        link.download = `Payroll_Run_${runData?.id || selectedRun?.id}_Payslips.csv`;
        link.click();
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ maxWidth: 1300, mx: 'auto', px: 3, py: 4 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>Payslip Distribution</Typography>
                        <Typography color="text.secondary">Release finalized payslips to staff or recall a released payroll run.</Typography>
                    </Box>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRuns}>Refresh</Button>
                </Stack>

                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    Finalized and paid runs support full-run distribution and individual payslip correction. Individual corrections recall only the selected employee until the approved replacement is released.
                </Alert>

                <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Table>
                        <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>
                            {['Run', 'Period', 'Type', 'Status', 'Personnel', 'Net Pay', 'Distribution', 'Actions'].map(label =>
                                <TableCell key={label} sx={{ fontWeight: 800 }}>{label}</TableCell>)}
                        </TableRow></TableHead>
                        <TableBody>
                            {runs.map(run => {
                                const releasedCount = Number(run.released_staff_count || 0);
                                const personnelCount = Number(run.staff_count || 0);
                                const distribution = releasedCount === 0 ? 'UNRELEASED' : releasedCount === personnelCount ? 'RELEASED' : 'PARTIAL';
                                return <TableRow key={run.id} hover>
                                    <TableCell sx={{ fontWeight: 700 }}>#{run.id}</TableCell>
                                    <TableCell>{String(run.period_month).padStart(2, '0')}/{run.period_year}</TableCell>
                                    <TableCell>{run.run_type}</TableCell>
                                    <TableCell><Chip size="small" color="success" label={run.status} /></TableCell>
                                    <TableCell>{run.staff_count}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{Number(run.total_net_pay || 0).toLocaleString()} MMK</TableCell>
                                    <TableCell><Chip size="small" color={distribution === 'RELEASED' ? 'primary' : distribution === 'PARTIAL' ? 'warning' : 'default'} variant={distribution === 'UNRELEASED' ? 'outlined' : 'filled'} label={`${distribution} (${releasedCount}/${personnelCount})`} /></TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" startIcon={<ViewIcon />} onClick={() => viewPayslips(run)}>View</Button>
                                            {distribution !== 'UNRELEASED' ?
                                                <Button size="small" color="error" startIcon={<RecallIcon />} onClick={() => changeRelease(run, false)}>Recall</Button> :
                                                <Button size="small" variant="contained" startIcon={<ReleaseIcon />} onClick={() => changeRelease(run, true)}>Release</Button>}
                                        </Stack>
                                    </TableCell>
                                </TableRow>;
                            })}
                            {!loading && runs.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: '#64748b' }}>No finalized payroll runs available.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </TableContainer>
                {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>}

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 5, mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>Payslip Corrections</Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            select
                            label="Month"
                            size="small"
                            value={periodFilter.month}
                            onChange={(e) => setPeriodFilter(prev => ({ ...prev, month: e.target.value }))}
                            sx={{ width: 140 }}
                        >
                            {months.map((m) => (
                                <MenuItem key={m.value} value={m.value}>
                                    {m.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Year"
                            size="small"
                            value={periodFilter.year}
                            onChange={(e) => setPeriodFilter(prev => ({ ...prev, year: e.target.value }))}
                            sx={{ width: 100 }}
                        >
                            {years.map((y) => (
                                <MenuItem key={y} value={y}>
                                    {y}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Button 
                            variant="outlined" 
                            size="small"
                            onClick={loadCorrections}
                            sx={{ height: 40 }}
                        >
                            Filter
                        </Button>
                    </Stack>
                </Stack>
                <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Table size="small"><TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>
                        {['ID','Period','Employee','Reason','Net Variance','Status','Actions'].map(label => <TableCell key={label} sx={{ fontWeight: 800 }}>{label}</TableCell>)}
                    </TableRow></TableHead><TableBody>
                        {corrections.map(c => (
                            <TableRow key={c.id}>
                                <TableCell>#{c.id}</TableCell>
                                <TableCell>{String(c.period_month).padStart(2,'0')}/{c.period_year}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.employee_number || c.staff_code} — {c.full_name}</TableCell>
                                <TableCell>{c.reason}</TableCell>
                                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: Number(c.net_variance) < 0 ? 'error.main' : 'success.main' }}>{Number(c.net_variance || 0).toLocaleString()} MMK</TableCell>
                                <TableCell><Chip size="small" label={c.status} /></TableCell>
                                <TableCell><Stack direction="row" spacing={0.5}>
                                    {['DRAFT','REJECTED'].includes(c.status) && <Button size="small" onClick={() => correctionAction(c,'SUBMITTED')}>Submit</Button>}
                                    {['DRAFT','REJECTED'].includes(c.status) && <Button size="small" onClick={() => editCorrection(c)}>Edit</Button>}
                                    {c.status === 'SUBMITTED' && <><Button size="small" color="success" onClick={() => correctionAction(c,'APPROVED')}>Approve</Button><Button size="small" color="error" onClick={() => correctionAction(c,'REJECTED')}>Reject</Button></>}
                                    {c.status === 'APPROVED' && <Button size="small" variant="contained" onClick={() => correctionAction(c,'APPLY')}>Apply</Button>}
                                    {c.status === 'APPLIED' && <Button size="small" variant="contained" onClick={() => correctionAction(c,'RELEASE')}>Release</Button>}
                                    {['APPLIED','RELEASED'].includes(c.status) && c.accounting_status !== 'NOT_REQUIRED' && <Button size="small" onClick={() => exportCorrection(c)}>{c.accounting_status === 'EXPORTED' ? 'Re-export Delta' : 'Export Delta'}</Button>}
                                </Stack></TableCell>
                            </TableRow>
                        ))}
                        {!corrections.length && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#64748b' }}>No payslip corrections.</TableCell></TableRow>}
                    </TableBody></Table>
                </TableContainer>
            </Box>

            {/* View Payslips for Payroll Run Dialog */}
            <Dialog open={!!selectedRun} onClose={() => { setSelectedRun(null); setPayslipSearch(''); }} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ pb: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>Payslips — Run #{selectedRun?.id}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Period: {String(selectedRun?.period_month).padStart(2, '0')}/{selectedRun?.period_year} ({filteredPayslips.length} of {payslips.length} payslips)
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <TextField
                                size="small"
                                placeholder="Search Emp No, Name, Dept..."
                                value={payslipSearch}
                                onChange={(e) => setPayslipSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: payslipSearch ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setPayslipSearch('')}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                                sx={{ width: 260 }}
                            />
                            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => exportToCSV(selectedRun, filteredPayslips)}>
                                CSV
                            </Button>
                            <Button size="small" variant="contained" startIcon={<PdfIcon />} onClick={() => generatePayslipsPDF(selectedRun, filteredPayslips)}>
                                PDF
                            </Button>
                        </Stack>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <TableContainer sx={{ maxHeight: '65vh' }}>
                        <Table size="small" stickyHeader sx={{ minWidth: 950 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, width: 90, whiteSpace: 'nowrap', bgcolor: '#f8fafc' }}>Emp No</TableCell>
                                    <TableCell sx={{ fontWeight: 700, minWidth: 180, whiteSpace: 'nowrap', bgcolor: '#f8fafc' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 700, width: 70, whiteSpace: 'nowrap', bgcolor: '#f8fafc' }}>Version</TableCell>
                                    <TableCell sx={{ fontWeight: 700, width: 110, whiteSpace: 'nowrap', bgcolor: '#f8fafc' }}>Distribution</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, minWidth: 120, whiteSpace: 'nowrap', bgcolor: '#f8fafc' }}>Gross</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, minWidth: 120, whiteSpace: 'nowrap', bgcolor: '#f8fafc' }}>Deductions</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, minWidth: 130, whiteSpace: 'nowrap', bgcolor: '#f8fafc' }}>Net Pay</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, width: 160, whiteSpace: 'nowrap', bgcolor: '#f8fafc' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredPayslips.map(payslip => (
                                    <TableRow key={payslip.id} hover>
                                        <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: '#1e293b' }}>
                                            {payslip.employee_number || payslip.staff_code || '-'}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                            {payslip.full_name}
                                            {payslip.dept_name && (
                                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 400 }}>
                                                    {payslip.dept_name}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <Chip size="small" variant="outlined" label={`v${payslip.version_number || 1}`} sx={{ height: 22, fontSize: '0.72rem' }} />
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <Chip
                                                size="small"
                                                color={payslip.release_status === 'RELEASED' ? 'primary' : payslip.release_status === 'RECALLED' ? 'warning' : 'default'}
                                                variant={payslip.release_status === 'RELEASED' ? 'filled' : 'outlined'}
                                                label={payslip.release_status || 'NOT_RELEASED'}
                                                sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600 }}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {Number(payslip.gross_pay || 0).toLocaleString()} MMK
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: (Number(payslip.gross_pay || 0) - Number(payslip.net_pay || 0)) > 0 ? 'error.main' : 'text.primary' }}>
                                            {(Number(payslip.gross_pay || 0) - Number(payslip.net_pay || 0)).toLocaleString()} MMK
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap', color: '#0f172a' }}>
                                            {Number(payslip.net_pay || 0).toLocaleString()} MMK
                                        </TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                <Button size="small" variant="outlined" startIcon={<ViewIcon />} onClick={() => handleOpenPayslipModal(payslip)}>
                                                    Payslip
                                                </Button>
                                                <Button size="small" color="warning" onClick={() => handleOpenCorrect(payslip)}>
                                                    Correct
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredPayslips.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#64748b' }}>
                                            {payslipSearch ? `No payslips matching "${payslipSearch}"` : 'No payslips found for this run.'}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => { setSelectedRun(null); setPayslipSearch(''); }}>Close</Button>
                    {Number(selectedRun?.is_released) === 1 ?
                        <Button color="error" startIcon={<RecallIcon />} onClick={() => changeRelease(selectedRun, false)}>Recall All</Button> :
                        <Button variant="contained" startIcon={<ReleaseIcon />} onClick={() => changeRelease(selectedRun, true)}>Release All</Button>}
                </DialogActions>
            </Dialog>

            {/* Individual Employee Payslip Voucher & Calculation Details Modal */}
            <Dialog open={!!breakdownPayroll} onClose={() => setBreakdownPayroll(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                Payslip — {breakdownPayroll?.full_name || ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {breakdownPayroll?.employee_number || breakdownPayroll?.staff_code || ''} · {breakdownPayroll?.job_title || ''} {breakdownPayroll?.dept_name ? `(${breakdownPayroll.dept_name})` : ''}
                            </Typography>
                        </Box>
                        <Tabs value={payslipTab} onChange={(_, val) => setPayslipTab(val)} sx={{ minHeight: 36 }}>
                            <Tab icon={<PayslipIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Payslip View" sx={{ minHeight: 36, py: 0.5, fontWeight: 700 }} />
                            <Tab icon={<AuditIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Calculation Audit" sx={{ minHeight: 36, py: 0.5, fontWeight: 700 }} />
                        </Tabs>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
                    {breakdownPayroll && payslipTab === 0 && (
                        <Paper elevation={0} sx={{ p: 3.5, border: '1px solid #e2e8f0', borderRadius: 3, bgcolor: '#ffffff' }}>
                            {/* Payslip Header */}
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 2.5, mb: 2.5, borderBottom: '2px solid #0f172a' }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    {tenantProfile?.logo_url && (
                                        <Box component="img" src={tenantProfile.logo_url} alt="Logo" sx={{ height: 44, maxWidth: 120, objectFit: 'contain' }} />
                                    )}
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                                            {tenantProfile?.tenant_name || 'PeopleHub'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
                                            CONFIDENTIAL PAYSLIP
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Chip
                                        label={breakdownPayroll.release_status || 'NOT_RELEASED'}
                                        color={breakdownPayroll.release_status === 'RELEASED' ? 'primary' : 'default'}
                                        size="small"
                                        sx={{ fontWeight: 700, mb: 0.5 }}
                                    />
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                        Period: {String(selectedRun?.period_month || '').padStart(2, '0')}/{selectedRun?.period_year}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Version: v{breakdownPayroll.version_number || 1}
                                    </Typography>
                                </Box>
                            </Stack>

                            {/* Employee Details Box */}
                            <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2, mb: 3, border: '1px solid #e2e8f0' }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Employee No</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                            {breakdownPayroll.employee_number || breakdownPayroll.staff_code || '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Employee Name</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                            {breakdownPayroll.full_name || '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Department</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                            {breakdownPayroll.dept_name || '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Designation</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                            {breakdownPayroll.job_title || '-'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* 2-Column Earnings & Deductions Tables */}
                            <Grid container spacing={2.5}>
                                {/* Earnings Column */}
                                <Grid item xs={12} md={6}>
                                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#ecfdf5' }}>
                                                    <TableCell sx={{ fontWeight: 800, color: '#065f46' }}>Earnings & Allowances</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 800, color: '#065f46' }}>Amount (MMK)</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ py: 0.8, color: '#334155' }}>Basic Salary</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        {Number(breakdownPayroll.basic_salary || 0).toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                                {Number(breakdownPayroll.ot_pay || 0) > 0 && (
                                                    <TableRow>
                                                        <TableCell sx={{ py: 0.8, color: '#334155' }}>
                                                            Overtime Pay
                                                            {Number(breakdownPayroll.payable_ot_mins || 0) > 0 && (
                                                                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                                                                    ({(Number(breakdownPayroll.payable_ot_mins) / 60).toFixed(1)} hrs)
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                            {Number(breakdownPayroll.ot_pay).toLocaleString()}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {Number(breakdownPayroll.meal_allowance || 0) > 0 && (
                                                    <TableRow>
                                                        <TableCell sx={{ py: 0.8, color: '#334155' }}>Meal Allowance</TableCell>
                                                        <TableCell align="right">{Number(breakdownPayroll.meal_allowance).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                )}
                                                {Number(breakdownPayroll.transport_allowance || 0) > 0 && (
                                                    <TableRow>
                                                        <TableCell sx={{ py: 0.8, color: '#334155' }}>Transport Allowance</TableCell>
                                                        <TableCell align="right">{Number(breakdownPayroll.transport_allowance).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                )}
                                                {Number(breakdownPayroll.special_allowance || 0) > 0 && (
                                                    <TableRow>
                                                        <TableCell sx={{ py: 0.8, color: '#334155' }}>Special Allowance</TableCell>
                                                        <TableCell align="right">{Number(breakdownPayroll.special_allowance).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                )}
                                                {(breakdownPayroll.recurring_items || []).filter(it => it.element_type === 'EARNING' && !['BASIC', 'OT'].includes(it.element_code)).map(item => (
                                                    <TableRow key={`earn-${item.element_id || item.element_code}`}>
                                                        <TableCell sx={{ py: 0.8, color: '#334155' }}>{item.element_name || item.element_code}</TableCell>
                                                        <TableCell align="right">{Number(item.amount || 0).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {(() => {
                                                    const recurringEarnings = (breakdownPayroll.recurring_items || []).filter(it => it.element_type === 'EARNING' && !['BASIC', 'OT'].includes(it.element_code));
                                                    const recurringEarningsSum = recurringEarnings.reduce((acc, it) => acc + Number(it.amount || 0), 0);
                                                    const unallocatedOtherEarnings = Math.max(0, Number(breakdownPayroll.other_earnings || 0) - recurringEarningsSum);
                                                    return unallocatedOtherEarnings > 0 ? (
                                                        <TableRow>
                                                            <TableCell sx={{ py: 0.8, color: '#334155' }}>Other Earnings</TableCell>
                                                            <TableCell align="right">{Number(unallocatedOtherEarnings).toLocaleString()}</TableCell>
                                                        </TableRow>
                                                    ) : null;
                                                })()}
                                            </TableBody>
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                                                    <TableCell sx={{ fontWeight: 800, color: '#166534' }}>Total Gross Earnings</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 800, color: '#166534', whiteSpace: 'nowrap' }}>
                                                        {Number(breakdownPayroll.gross_pay || 0).toLocaleString()} MMK
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                        </Table>
                                    </TableContainer>
                                </Grid>

                                {/* Deductions Column */}
                                <Grid item xs={12} md={6}>
                                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#fef2f2' }}>
                                                    <TableCell sx={{ fontWeight: 800, color: '#991b1b' }}>Deductions & Taxes</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 800, color: '#991b1b' }}>Amount (MMK)</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Number(breakdownPayroll.late_early_penalties || 0) > 0 && (
                                                    <TableRow>
                                                        <TableCell sx={{ py: 0.8, color: '#334155' }}>Late / Early Penalties</TableCell>
                                                        <TableCell align="right">{Number(breakdownPayroll.late_early_penalties).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                )}
                                                {Number(breakdownPayroll.unpaid_leave_deduction || 0) > 0 && (
                                                    <TableRow>
                                                        <TableCell sx={{ py: 0.8, color: '#334155' }}>
                                                            Unpaid Leave Deduction
                                                            {Number(breakdownPayroll.unpaid_leave_days || 0) > 0 && (
                                                                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                                                                    ({breakdownPayroll.unpaid_leave_days} d)
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">{Number(breakdownPayroll.unpaid_leave_deduction).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                )}
                                                {Number(breakdownPayroll.unpaid_absence_deduction || 0) > 0 && (
                                                    <TableRow>
                                                        <TableCell sx={{ py: 0.8, color: '#334155' }}>
                                                            Unpaid Absence Deduction
                                                            {Number(breakdownPayroll.unpaid_absence_days || 0) > 0 && (
                                                                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                                                                    ({breakdownPayroll.unpaid_absence_days} d)
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">{Number(breakdownPayroll.unpaid_absence_deduction).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                )}
                                                {Number(breakdownPayroll.ssb_deduction || 0) > 0 && (
                                                    <TableRow>
                                                        <TableCell sx={{ py: 0.8, color: '#334155' }}>SSB (Social Security 2%)</TableCell>
                                                        <TableCell align="right">{Number(breakdownPayroll.ssb_deduction).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                )}
                                                {Number(breakdownPayroll.tax_deduction || 0) > 0 && (
                                                    <TableRow>
                                                        <TableCell sx={{ py: 0.8, color: '#334155' }}>Income Tax (PIT)</TableCell>
                                                        <TableCell align="right">{Number(breakdownPayroll.tax_deduction).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                )}
                                                {(breakdownPayroll.recurring_items || []).filter(it => it.element_type === 'DEDUCTION').map(item => (
                                                    <TableRow key={`ded-${item.element_id || item.element_code}`}>
                                                        <TableCell sx={{ py: 0.8, color: '#334155' }}>{item.element_name || item.element_code}</TableCell>
                                                        <TableCell align="right">{Number(item.amount || 0).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {(() => {
                                                    const recurringDeductions = (breakdownPayroll.recurring_items || []).filter(it => it.element_type === 'DEDUCTION');
                                                    const recurringDeductionsSum = recurringDeductions.reduce((acc, it) => acc + Number(it.amount || 0), 0);
                                                    const unallocatedOtherDeductions = Math.max(0, Number(breakdownPayroll.other_deductions || 0) - recurringDeductionsSum);
                                                    return unallocatedOtherDeductions > 0 ? (
                                                        <TableRow>
                                                            <TableCell sx={{ py: 0.8, color: '#334155' }}>Other Deductions</TableCell>
                                                            <TableCell align="right">{Number(unallocatedOtherDeductions).toLocaleString()}</TableCell>
                                                        </TableRow>
                                                    ) : null;
                                                })()}
                                                {(Number(breakdownPayroll.gross_pay || 0) - Number(breakdownPayroll.net_pay || 0)) <= 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={2} align="center" sx={{ color: 'text.secondary', py: 1.5 }}>
                                                            No deductions applied
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#fff1f2' }}>
                                                    <TableCell sx={{ fontWeight: 800, color: '#be123c' }}>Total Deductions</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 800, color: '#be123c', whiteSpace: 'nowrap' }}>
                                                        {(Number(breakdownPayroll.gross_pay || 0) - Number(breakdownPayroll.net_pay || 0)).toLocaleString()} MMK
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            </Grid>

                            {/* Net Take-Home Pay Banner */}
                            <Box sx={{ mt: 3, p: 2.5, borderRadius: 2.5, bgcolor: '#ffffff', border: '1.5px solid #cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: '#475569', fontWeight: 800 }}>
                                        Net Take-Home Pay
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
                                        Gross Pay ({Number(breakdownPayroll.gross_pay || 0).toLocaleString()}) − Total Deductions ({(Number(breakdownPayroll.gross_pay || 0) - Number(breakdownPayroll.net_pay || 0)).toLocaleString()})
                                    </Typography>
                                </Box>
                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                    {Number(breakdownPayroll.net_pay || 0).toLocaleString()}{' '}
                                    <Typography component="span" variant="h6" sx={{ color: '#64748b', fontWeight: 700 }}>
                                        MMK
                                    </Typography>
                                </Typography>
                            </Box>
                        </Paper>
                    )}

                    {breakdownPayroll && payslipTab === 1 && (
                        <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 3, bgcolor: '#ffffff' }}>
                            <EffectiveDatedPayrollBreakdown payroll={breakdownPayroll} />
                        </Paper>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button startIcon={<PdfIcon />} onClick={() => generatePayslipsPDF(selectedRun, [breakdownPayroll])}>Download PDF</Button>
                    <Button startIcon={<PrintIcon />} onClick={() => window.print()}>Print</Button>
                    <Button onClick={() => setBreakdownPayroll(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Correct Payslip Modal */}
            <Dialog open={!!correctionPayslip} onClose={() => { setCorrectionPayslip(null); setEditingCorrection(null); }} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingCorrection
                        ? `Edit Correction #${editingCorrection.id}`
                        : `Correct Payslip — ${correctionPayslip?.employee_number || correctionPayslip?.staff_code || ''} (${correctionPayslip?.full_name || ''})`}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="warning">Creating the draft recalls only this employee’s released payslip. The original remains current until approval is applied.</Alert>
                        <TextField required label="Correction Reason" multiline rows={2} value={correctionForm.reason} onChange={e => setCorrectionForm({ ...correctionForm, reason: e.target.value })} />
                        <TextField
                            select
                            label="Component"
                            value={correctionForm.component_code}
                            onChange={e => handleComponentChange(e.target.value)}
                            SelectProps={{
                                renderValue: (selected) => {
                                    const item = allComponents.find(c => c.code === selected);
                                    return item ? `${item.name} (${item.code}) — ${item.effect}` : selected;
                                }
                            }}
                        >
                            <MenuItem disabled sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', bgcolor: '#f1f5f9' }}>
                                EARNINGS / ALLOWANCES
                            </MenuItem>
                            {allComponents.filter(c => c.effect === 'EARNING').map(c => (
                                <MenuItem key={`earn-${c.code}-${c.id || 'std'}`} value={c.code}>
                                    <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center" spacing={2}>
                                        <Typography variant="body2">{c.name} ({c.code})</Typography>
                                        <Chip size="small" color="success" variant="outlined" label="EARNING" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    </Stack>
                                </MenuItem>
                            ))}
                            <MenuItem disabled sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', bgcolor: '#f1f5f9', mt: 1 }}>
                                DEDUCTIONS
                            </MenuItem>
                            {allComponents.filter(c => c.effect === 'DEDUCTION').map(c => (
                                <MenuItem key={`ded-${c.code}-${c.id || 'std'}`} value={c.code}>
                                    <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center" spacing={2}>
                                        <Typography variant="body2">{c.name} ({c.code})</Typography>
                                        <Chip size="small" color="error" variant="outlined" label="DEDUCTION" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    </Stack>
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField required type="number" label="Adjustment Amount (+/- MMK)" value={correctionForm.amount_delta} onChange={e => setCorrectionForm({ ...correctionForm, amount_delta: e.target.value })} helperText="Use a positive amount to add/increase or a negative amount to reduce." />
                        <Stack direction="row" spacing={2}>
                            <TextField fullWidth type="number" label="Quantity / Hours" value={correctionForm.quantity} onChange={e => setCorrectionForm({ ...correctionForm, quantity: e.target.value })} />
                            <TextField fullWidth type="number" label="Rate" value={correctionForm.rate} onChange={e => setCorrectionForm({ ...correctionForm, rate: e.target.value })} />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => { setCorrectionPayslip(null); setEditingCorrection(null); }}>Cancel</Button>
                    <Button variant="contained" disabled={!correctionForm.reason.trim() || !Number(correctionForm.amount_delta)} onClick={createCorrection}>
                        {editingCorrection ? 'Save Draft' : 'Create Draft & Recall'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={message.open} autoHideDuration={5000} onClose={() => setMessage({ ...message, open: false })}>
                <Alert severity={message.severity} variant="filled">{message.text}</Alert>
            </Snackbar>
        </Box>
    );
};

export default PayslipDistribution;
